import $ from 'jquery';

import Swiper from 'swiper';
import { Navigation, Pagination, Autoplay, EffectFade, EffectCreative, EffectFlip, Parallax, A11y, Keyboard } from 'swiper/modules';

import 'swiper/css/parallax';
import 'swiper/css/effect-fade';
import 'swiper/css/effect-creative';
import 'swiper/css/effect-flip';

import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import 'swiper/css/a11y';
import '../../scss/vendor/swiper.scss';

import whenPageShown from '../components/whenPageShown';

let swiperInstances = [];

// accessibility and keyboard of every slider: buttons get a role and a label, the slides a group role, arrows keys work
// while the slider is in view (Swiper's A11y and Keyboard modules)
const messages = {
    de: {
        prevSlideMessage: 'Vorheriges Element',
        nextSlideMessage: 'Nächstes Element',
        firstSlideMessage: 'Das ist das erste Element',
        lastSlideMessage: 'Das ist das letzte Element',
        paginationBulletMessage: 'Zu Element {{index}}',
        slideLabelMessage: '{{index}} von {{slidesLength}}',
    },
    en: {
        prevSlideMessage: 'Previous slide',
        nextSlideMessage: 'Next slide',
        firstSlideMessage: 'This is the first slide',
        lastSlideMessage: 'This is the last slide',
        paginationBulletMessage: 'Go to slide {{index}}',
        slideLabelMessage: '{{index}} / {{slidesLength}}',
    },
    nl: {
        prevSlideMessage: 'Vorig element',
        nextSlideMessage: 'Volgend element',
        firstSlideMessage: 'Dit is het eerste element',
        lastSlideMessage: 'Dit is het laatste element',
        paginationBulletMessage: 'Naar element {{index}}',
        slideLabelMessage: '{{index}} van {{slidesLength}}',
    },
};
// language of the page (de when the attribute is missing), other languages get the English labels
const language = (document.documentElement.lang || 'de').slice(0, 2);
const accessible = {
    a11y: {
        enabled: true,
        ...(messages[language] ?? messages.en),
    },
    keyboard: { enabled: true, onlyInViewport: true },
};

export function initSwiper () {

    if ($(".swiper-container:not(.hero-swiper) .swiper-slide").length > 1) {
        $(".swiper-container:not(.hero-swiper)").each(function(index, element) {
            let id = $(this).attr("id");
            let loop = !!$(this).data("loop");
            let stopOnLastSlide = !$(this).data("loop");
            let isAutoplay = $(this).data("autoplay");
            let delay = $(this).data("delay") ?? 7500;
            let fade = $(this).data("fade") ? "fade" : "none";
            let parallax = $(this).data("parallax");

            let swiperConfig = {
                modules: [Navigation, Pagination, Autoplay, EffectFade, Parallax, A11y, Keyboard],
                ...accessible,
                slidesPerView: 1,
                spaceBetween: 0,
                speed: 800,
                parallax: parallax,
                effect: fade,
                fadeEffect: { crossFade: true },
                navigation: {
                    nextEl: "#next" + id,
                    prevEl: "#prev" + id
                },
                pagination: {
                    el: "#pagination" + id,
                    dynamicBullets: true,
                    clickable: true
                },
            };

            if(loop) {
                Object.assign(swiperConfig, {loop: true});
            }

            if(isAutoplay) {
                Object.assign(swiperConfig, {autoplay: {
                    delay: delay,
                    stopOnLastSlide: stopOnLastSlide,
                    disableOnInteraction: false,
                    pauseOnMouseEnter: true
                }});
            }

            swiperInstances.push(new Swiper("#"+ id, swiperConfig));

        });
    }
}

export function initHeroSwiper () {
    // The room only grows: the header shrinks and the info bar folds away once the page is scrolled (shrinkNavigation.js).
    // If the room followed that, the page would change its height while it scrolls; on a page that is hardly longer than the
    // window the scroll position would jump across the threshold again and again and the header would flicker. A resize
    // starts the measuring anew.
    let headerSpace = 0;
    const reserveFixedHeaderSpace = (event) => {
        const header = document.querySelector('#header header.fixed-top');
        const spacer = document.querySelector('#top.top-has-hero #spacer');

        if (!header) {
            return;
        }

        if (event && event.type === 'resize') {
            headerSpace = 0;
        }
        headerSpace = Math.max(headerSpace, Math.ceil(header.getBoundingClientRect().height));

        // the fullscreen profile is as high as the window minus the header (--hero-header-offset)
        document.documentElement.style.setProperty('--hero-header-offset', `${headerSpace}px`);

        if (spacer) {
            spacer.style.setProperty('height', `${headerSpace}px`, 'important');
        }
    };

    reserveFixedHeaderSpace();
    window.addEventListener('resize', reserveFixedHeaderSpace, { passive: true });
    const fixedHeader = document.querySelector('#header header.fixed-top');
    if (fixedHeader && 'ResizeObserver' in window) {
        new ResizeObserver(reserveFixedHeaderSpace).observe(fixedHeader);
    }

    $('.hero-swiper').each(function(index, element) {
        const $hero = $(element);
        const slideCount = Number($hero.attr('data-authored-slide-count')) || $hero.find('.swiper-slide:not(.swiper-slide-duplicate)').length;

        // "Schwung": the choreography of the former banner (cslider): the layers of the old slide fly out one after the other, those
        // of the new slide fly in; Swiper only stacks the slides (effect fade) and counts. The keyframes are in _hero-swing.scss.
        const choreography = $hero.attr('data-choreography') === '1';

        if (slideCount < 2) {
            // One slide needs no Swiper. The choreography hides every slide without Swiper's class swiper-slide-active
            // (_hero-swing.scss), so the only slide gets it here and flies in once the preloader is gone.
            if (choreography) {
                const only = element.querySelector('.hero-slide');
                if (only) {
                    $hero.addClass('hero-pending');
                    only.classList.add('swiper-slide-active', 'hero-slide-active');
                    whenPageShown(() => {
                        $hero.removeClass('hero-pending');
                        only.classList.add('hero-entering', 'hero-dir-next', 'hero-first');
                        window.setTimeout(() => only.classList.remove('hero-entering', 'hero-dir-next', 'hero-first'), 2100);
                    });
                }
            }

            return;
        }

        const id = $hero.attr('id');
        const loop = Boolean($hero.data('loop'));
        const autoplay = Boolean($hero.data('autoplay'));
        const delay = Number($hero.data('delay')) || 7500;
        const fade = Boolean($hero.data('fade'));
        const useParallax = Boolean($hero.data('parallax'));
        const bandTransitionDuration = Math.max(200, Number($hero.attr('data-band-transition-duration')) || 1400);
        const bandDirection = $hero.attr('data-band-direction') === 'right' ? 1 : -1;
        const band = $hero.find('.hero-band').get(0);
        const easingMap = {
            linear: 'linear',
            ease: 'ease',
            'ease-in': 'ease-in',
            'ease-out': 'ease-out',
            // floating: slow start, slow end
            'ease-in-out': 'cubic-bezier(.65, 0, .35, 1)',
            spring: 'linear(0, 0.04, 0.1366, 0.2619, 0.396, 0.5256, 0.6429, 0.7435, 0.8263, 0.8915, 0.9408, 0.9764, 1.0006, 1.0159, 1.0244, 1.0279, 1.0281, 1.0261, 1.0229, 1.0192, 1.0154, 1.0119, 1.0088, 1.0061, 1.004, 1.0024, 1.0012, 1.0003, 0.9998, 0.9994, 0.9992, 0.9992, 0.9992, 0.9993, 0.9994, 0.9995, 1)',
            bounce: 'linear(0, 0.0486, 0.1685, 0.3258, 0.4943, 0.6549, 0.7955, 0.9097, 0.9955, 1.0542, 1.0891, 1.1047, 1.1058, 1.0968, 1.0819, 1.0643, 1.0464, 1.0301, 1.0162, 1.0053, 0.9974, 0.9923, 0.9895, 0.9886, 0.989, 0.9903, 0.992, 0.994, 0.9958, 0.9975, 0.9988, 0.9998, 1.0005, 1.001, 1.0012, 1.0012, 1)',
            smooth: 'cubic-bezier(.22, 1, .36, 1)',
            'ease-in-cubic': 'cubic-bezier(.32, 0, .67, 0)',
            'ease-out-cubic': 'cubic-bezier(.33, 1, .68, 1)',
            'ease-in-out-cubic': 'cubic-bezier(.65, 0, .35, 1)',
        };
        const bandEasing = easingMap[$hero.attr('data-band-easing')] || easingMap.smooth;
        // how the slides move in (setting "Motion of the slides"). The slide track always moves with one calm curve (a
        // springy track would swing the whole slide over its edge). The parallax layers (title, text, image) get their own
        // curve and their own start delay: a later layer has less time for its way and so moves faster. "spring" and
        // "bounce" are the curves of the effect profiles (Federnd, Titschend), the layers overshoot a little at the end.
        const calmTrack = easingMap.smooth;
        const slideMotions = {
            smooth: { speed: 1500, track: calmTrack, title: calmTrack, text: calmTrack, image: calmTrack, delay: [0, 0, 0] },
            even: { speed: 1400, track: easingMap['ease-in-out'], title: easingMap['ease-in-out'], text: easingMap['ease-in-out'], image: easingMap['ease-in-out'], delay: [0, 0, 0] },
            calm: { speed: 2200, track: 'cubic-bezier(.45, 0, .25, 1)', title: 'cubic-bezier(.45, 0, .25, 1)', text: 'cubic-bezier(.45, 0, .25, 1)', image: 'cubic-bezier(.45, 0, .25, 1)', delay: [0, 0, 0] },
            spring: { speed: 1600, track: calmTrack, title: calmTrack, text: easingMap.spring, image: easingMap.spring, delay: [0, 90, 180] },
            bounce: { speed: 1700, track: calmTrack, title: easingMap.spring, text: easingMap.bounce, image: easingMap.bounce, delay: [0, 100, 220] },
        };
        // speed 0: Swiper changes the slide at once and does not wait for a transition that never runs (the CSS animates the layers);
        // the clicks and swipes that come during the sequence are held back by allowSlideNext/Prev (startChoreography)
        slideMotions.swing = { speed: 0, track: calmTrack, title: calmTrack, text: calmTrack, image: calmTrack, delay: [0, 0, 0] };
        const slideMotion = { ...(slideMotions[$hero.attr('data-slide-motion')] || slideMotions.spring) };
        element.style.setProperty('--hero-motion-ease', slideMotion.track);
        ['title', 'text', 'image'].forEach((layer, i) => {
            element.style.setProperty('--hero-ease-' + layer, slideMotion[layer]);
            element.style.setProperty('--hero-delay-' + layer, slideMotion.delay[i] + 'ms');
        });
        // motion "like the effect profile": the template sends the curve, duration and start delay of every layer from the
        // profile (data-layer-fx, see hero.swiper.html.twig). They replace the values of the preset; the slide track takes
        // the longest duration so that no layer is still moving when the next slide is already there.
        let layerFx = null;
        try {
            layerFx = JSON.parse($hero.attr('data-layer-fx') || 'null');
        } catch (error) {
            layerFx = null;
        }
        if (layerFx) {
            let longest = 0;
            ['title', 'text', 'links', 'image'].forEach((layer) => {
                const fx = layerFx[layer];
                if (!fx) {
                    return;
                }
                longest = Math.max(longest, Number(fx.duration) || 0);
                element.style.setProperty('--hero-ease-' + layer, easingMap[fx.ease] || calmTrack);
                element.style.setProperty('--hero-delay-' + layer, (Number(fx.delay) || 0) + 'ms');
                element.style.setProperty('--hero-dur-' + layer, Math.max(200, Number(fx.duration) || 0) + 'ms');
            });
            if (longest > 0) {
                slideMotion.speed = Math.max(400, longest);
            }
            element.classList.add('hero-swiper--profile');
        }
        const normalizeEffect = (effect) => {
            if (effect === 'none' || effect === 'fade' || effect === 'counter') return effect;
            if (effect.includes('right')) return 'fade-right';
            if (effect.includes('left')) return 'fade-left';
            if (effect.includes('up')) return 'fade-up';
            if (effect.includes('down')) return 'fade-down';
            return 'fade-up';
        };
        const applyLayerEffects = (instance) => {
            instance.slides.forEach((slide) => {
                const duration = Math.max(100, Number(slide.dataset.heroEffectDuration) || 1600);
                slide.dataset.heroEffect = normalizeEffect(slide.dataset.heroEffect || 'none');
                slide.style.setProperty('--hero-effect-duration', `${duration}ms`);
                slide.style.setProperty('--hero-image-effect-duration', `${Math.round(duration * 1.2)}ms`);
                slide.style.setProperty('--hero-effect-easing', easingMap[slide.dataset.heroEffectEasing] || 'ease-in-out');
                slide.querySelectorAll('.hero-slide__motion-layer').forEach((layer) => {
                    const layerOrder = layer.classList.contains('hero-slide__title') ? 0
                        : layer.classList.contains('hero-slide__text') ? 1
                            : layer.classList.contains('hero-slide__foreground') ? 2
                                : layer.classList.contains('hero-slide__logo') ? 3 : 4;
                    layer.style.setProperty('--hero-layer-delay', `${450 + (slide.dataset.heroEffectMulti === '1' ? layerOrder * (Number(slide.dataset.heroEffectStep) || 250) : 0)}ms`);
                });
            });
        };
        const bandStep = slideCount < 6 ? 100 / slideCount : 20;
        let bandCycle = 0;
        let hasBandPosition = false;
        const moveBand = (instance) => {
            if (!band) {
                return;
            }

            const realIndex = instance.realIndex;
            if (hasBandPosition) {
                if (realIndex === 0 && instance.previousRealIndex === slideCount - 1) {
                    bandCycle += slideCount;
                } else if (realIndex === slideCount - 1 && instance.previousRealIndex === 0) {
                    bandCycle -= slideCount;
                }
            }

            band.style.transitionDuration = `${bandTransitionDuration}ms`;
            // the former banner: the background starts 0.3 s after the layers began to leave and moves ease-in-out
            band.style.transitionDelay = choreography ? '300ms' : '0ms';
            band.style.transitionTimingFunction = choreography ? easingMap['ease-in-out'] : bandEasing;
            band.style.backgroundPositionX = `${bandDirection * bandStep * (bandCycle + realIndex)}vw`;
            hasBandPosition = true;
        };
        // --- choreography ("Schwung") ---------------------------------------------------------------------------------
        // A change adds hero-leaving to the old and hero-entering to the new slide, both with hero-dir-next or hero-dir-prev;
        // the keyframes take over. The classes go when the sequence is over (CHOREOGRAPHY_TIME). The first slide flies in
        // when the preloader is gone (hero-first: shorter delay, nothing is leaving).
        const CHOREOGRAPHY_TIME = 2100;
        const choreographyClasses = 'hero-leaving hero-entering hero-dir-next hero-dir-prev hero-first';
        let choreographyTimer = null;
        const clearChoreography = (instance) => {
            window.clearTimeout(choreographyTimer);
            instance.slides.forEach((slide) => slide.classList.remove(...choreographyClasses.split(' ')));
        };
        // until the sequence is over a click, a swipe or the autoplay does not start the next change (the old slide is still leaving)
        const holdChanges = (instance, hold) => {
            instance.allowSlideNext = !hold;
            instance.allowSlidePrev = !hold;
        };
        const startChoreography = (instance, leaving, direction, first = false) => {
            clearChoreography(instance);
            if (leaving && leaving !== instance.slides[instance.activeIndex]) {
                leaving.classList.add('hero-leaving', 'hero-dir-' + direction);
            }
            const entering = instance.slides[instance.activeIndex];
            if (entering) {
                // restart the animations of a slide that was shown before (the classes were removed one frame ago)
                void entering.offsetWidth;
                entering.classList.add('hero-entering', 'hero-dir-' + direction);
                if (first) {
                    entering.classList.add('hero-first');
                }
            }
            if (!first) {
                holdChanges(instance, true);
            }
            choreographyTimer = window.setTimeout(() => {
                clearChoreography(instance);
                holdChanges(instance, false);
            }, CHOREOGRAPHY_TIME);
        };
        const changeDirection = (instance) => (instance.activeIndex >= instance.previousIndex ? 'next' : 'prev');
        // Own autoplay: the one of Swiper restarts its timer when the transition of the wrapper ends, and the choreography has
        // none. The time counts from the last change; it waits while the mouse is over the banner or the tab is hidden.
        let autoTimer = null;
        let hovered = false;
        const scheduleAuto = (instance) => {
            window.clearTimeout(autoTimer);
            if (!choreography || !autoplay) {
                return;
            }
            autoTimer = window.setTimeout(() => {
                if (!hovered && !document.hidden) {
                    if (!loop && instance.isEnd) {
                        instance.slideTo(0, 0);
                    } else {
                        instance.slideNext(0);
                    }
                }
                scheduleAuto(instance);
            }, delay);
        };
        if (choreography) {
            $hero.on('mouseenter', () => { hovered = true; }).on('mouseleave', () => { hovered = false; });
        }
        // the preloader covers the page at first: the content of the first slide only flies in once the preloader is gone
        $hero.addClass('hero-pending');
        const swiper = new Swiper('#' + id, {
            modules: [Navigation, Pagination, Autoplay, EffectFade, Parallax, A11y, Keyboard],
            ...accessible,
            slidesPerView: 1,
            speed: slideMotion.speed,
            loop,
            // the choreography stacks the slides like the fade effect, but nothing fades by itself (the CSS decides)
            effect: fade || choreography ? 'fade' : 'slide',
            fadeEffect: { crossFade: !choreography },
            parallax: useParallax,
            watchSlidesProgress: true,
            navigation: {
                nextEl: '#next' + id,
                prevEl: '#prev' + id,
            },
            pagination: {
                el: '#pagination' + id,
                clickable: true,
            },
            autoplay: autoplay && !choreography ? {
                delay,
                disableOnInteraction: false,
                pauseOnMouseEnter: true,
            } : false,
            on: {
                // while a finger or the mouse drags the slide the layers follow at once, the start delay only counts for the movement itself
                sliderFirstMove(instance) {
                    instance.el.classList.add('is-dragging');
                },
                transitionStart(instance) {
                    instance.el.classList.remove('is-dragging');
                },
                init(instance) {
                    applyLayerEffects(instance);
                    moveBand(instance);
                    $(instance.slides[instance.activeIndex]).addClass('hero-slide-active');
                },
                slideChangeTransitionStart(instance) {
                    moveBand(instance);
                    if (choreography) {
                        startChoreography(instance, instance.slides[instance.previousIndex], changeDirection(instance));
                        scheduleAuto(instance);
                    }
                    $(instance.slides).removeClass('hero-slide-active');
                    $(instance.slides[instance.activeIndex]).addClass('hero-slide-active');
                },
            },
        });

        swiperInstances.push(swiper);
        whenPageShown(() => {
            $hero.removeClass('hero-pending');
            if (choreography) {
                startChoreography(swiper, null, 'next', true);
                scheduleAuto(swiper);
            }
        });
    });
}

// "Bildwechsel" of the single image slider (effect profile or image block, EffectProfileResolver::imageTransition()):
// the Swiper effect and its speed. Visitors who ask for less motion always get the cross-fade.
const imageTransitions = {
    fade: { speed: 2000, effect: 'fade', fadeEffect: { crossFade: true } },
    slide: { speed: 900, effect: 'slide' },
    // the old image slips a little to the side and fades, the new one pushes in
    glide: { speed: 1100, effect: 'creative', creativeEffect: { prev: { translate: ['-30%', 0, -1], opacity: 0 }, next: { translate: ['100%', 0, 0] } } },
    // turns away to the side like a card swept off the table, faded out before it reaches the edge of the slider (it clips)
    rotate: { speed: 1400, effect: 'creative', creativeEffect: { prev: { translate: ['-45%', 0, -300], rotate: [0, 0, -10], opacity: 0 }, next: { translate: ['45%', 0, -300], rotate: [0, 0, 10], opacity: 0 } } },
    flip: { speed: 1100, effect: 'flip', flipEffect: { slideShadows: false, limitRotation: true } },
    // the old image grows and fades, the new one comes up from a little smaller
    zoom: { speed: 1300, effect: 'creative', creativeEffect: { prev: { scale: 1.25, opacity: 0 }, next: { scale: 0.8, opacity: 0 } } },
};
const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

export function initSingleImageSwiper () {
    if ($(".single-image-swiper-container .swiper-slide").length > 1) {
        $(".single-image-swiper-container").each(function (index, element) {
            let id = $(this).attr("id");
            let loop = !!$(this).data("loop");
            let stopOnLastSlide = !$(this).data("loop");
            let isAutoplay = $(this).data("autoplay");
            let delay = $(this).data("delay") ?? 7500;
            // "random select" of the image block: every visit starts at another image (in the browser, so a cached page varies too)
            let initialSlide = $(this).data("random-start") ? Math.floor(Math.random() * $(this).find(".swiper-slide").length) : 0;
            let transition = reducedMotion.matches ? imageTransitions.fade : (imageTransitions[$(this).data("transition")] ?? imageTransitions.fade);

            let swiperConfig = {
                modules: [Navigation, Pagination, Autoplay, EffectFade, EffectCreative, EffectFlip, A11y, Keyboard],
                ...accessible,
                slidesPerView: 1,
                spaceBetween: 16,
                initialSlide: initialSlide,
                loop: loop,
                ...transition,
                navigation: {
                    nextEl: "#next" + id,
                    prevEl: "#prev" + id
                },
                pagination: {
                    el: "#pagination" + id,
                    dynamicBullets: true,
                    clickable: true
                },
            };

            if (isAutoplay) {
                Object.assign(swiperConfig, {
                    autoplay: {
                        delay: delay,
                        stopOnLastSlide: stopOnLastSlide,
                        disableOnInteraction: false,
                        pauseOnMouseEnter: true
                    }
                });
            }

            swiperInstances.push(new Swiper("#" + id, swiperConfig));

        });
    }
}

export function initFullImageSwiper () {
    if ($(".full-image-swiper-container .swiper-slide").length > 1) {
        $(".full-image-swiper-container").each(function (index, element) {
            let id = $(this).attr("id");
            let loop = !!$(this).data("loop");
            let stopOnLastSlide = !$(this).data("loop");
            let isAutoplay = $(this).data("autoplay");
            let delay = $(this).data("delay") ?? 7500;
            // the longread hero (wideFormat, see swiper-gallery.html.twig) reuses this same container class for
            // its full-bleed slideshow - it always shows one slide at a time, never the gallery's 2-3-up grid
            let single = $(this).hasClass("full-image-swiper-container--single");

            let swiperConfig = {
                modules: [Navigation, Pagination, Autoplay, A11y, Keyboard],
                ...accessible,
                slidesPerView: 1,
                spaceBetween: single ? 0 : 24,
                speed: 200,
                loop: loop,
                navigation: {
                    nextEl: "#next" + id,
                    prevEl: "#prev" + id
                },
                pagination: {
                    el: "#pagination" + id,
                    dynamicBullets: true,
                    clickable: true
                },
            };

            if (!single) {
                Object.assign(swiperConfig, {
                    breakpoints: {
                        768: {
                            slidesPerView: 2,
                            spaceBetween: 32,
                        },
                        992: {
                            slidesPerView: 2,
                            spaceBetween: 32,
                        },
                        1200: {
                            slidesPerView: 3,
                            spaceBetween: 32,
                        },
                    },
                });
            }

            if (isAutoplay) {
                Object.assign(swiperConfig, {
                    autoplay: {
                        delay: delay,
                        stopOnLastSlide: stopOnLastSlide,
                        disableOnInteraction: false,
                        pauseOnMouseEnter: true
                    }
                });
            }

            swiperInstances.push(new Swiper("#" + id, swiperConfig));

        });
    }
}

export function initNewsSwiper () {
    if ($(".news-swiper-container .swiper-slide").length > 1) {
        $(".news-swiper-container").each(function (index, element) {
            let id = $(this).attr("id");
            let loop = !!$(this).data("loop");
            let stopOnLastSlide = !$(this).data("loop");
            let isAutoplay = $(this).data("autoplay");
            let delay = $(this).data("delay") ?? 7500;
            // cards side by side (collection profile, data-per-view): "grid" follows the card grid of the plain list
            // (col-md-6 col-xl-4 col-xxl-3), the others stop earlier; the image sizes of the cards match these steps
            // (blocks/main/collection.html.twig)
            const perViewSteps = {
                grid: { 768: 2, 1200: 3, 1400: 4 },
                max3: { 768: 2, 1200: 3 },
                max2: { 768: 2 },
                single: {},
            };
            // small tiles: two on a phone, up to six on a wide screen (like the grid col-6 col-md-4 col-lg-3 col-xxl-2)
            const smallSteps = {
                grid: { 0: 2, 768: 3, 992: 4, 1400: 6 },
                max3: { 0: 2, 768: 3 },
                max2: { 0: 2 },
            };
            const perView = $(this).data("per-view");
            const small = "small" === $(this).data("tile-size");
            const steps = (small ? smallSteps[perView] : null) ?? perViewSteps[perView] ?? perViewSteps.grid;
            const breakpoints = {};
            Object.entries(steps).forEach(([width, slidesPerView]) => {
                breakpoints[width] = { slidesPerView };
            });

            let swiperConfig = {
                modules: [Navigation, Pagination, Autoplay, A11y, Keyboard],
                ...accessible,
                slidesPerView: 1,
                spaceBetween: small ? 16 : 24,
                speed: 400,
                breakpoints: breakpoints,
                // fewer cards than places: centred, like the plain grid (justify-content-center)
                centerInsufficientSlides: true,
                loop: loop,
                navigation: {
                    nextEl: "#next" + id,
                    prevEl: "#prev" + id
                },
                pagination: {
                    el: "#pagination" + id,
                    dynamicBullets: true,
                    clickable: true
                },
            };

            if (isAutoplay) {
                Object.assign(swiperConfig, {
                    autoplay: {
                        delay: delay,
                        stopOnLastSlide: stopOnLastSlide,
                        disableOnInteraction: false,
                        pauseOnMouseEnter: true
                    }
                });
            }

            swiperInstances.push(new Swiper("#" + id, swiperConfig));

        });
    }
}